import type { DeploymentRecord } from '@dev-console/types';
import { useEffect } from 'react';

import {
  canRollbackDeployment,
  formatClock,
  formatDuration,
  relativeTime,
  totalDurationSeconds,
} from '@/components/deployments/deployment-format';
import { DeploymentPullRequests } from '@/components/deployments/deployment-pull-requests';
import { DeploymentTimeline } from '@/components/deployments/deployment-timeline';

const ACTION_BUTTON_CLASS =
  'inline-flex h-11 w-full items-center justify-center rounded-md px-3 text-sm font-medium sm:h-8 sm:w-auto sm:px-2.5 sm:text-xs';

const GITHUB_COMMIT_URL = 'https://github.com/long-view-labs/mono-repo/commit';

function commitMessageMatchesPullRequest(
  commitMessage: string,
  pullRequests: DeploymentRecord['pullRequests']
): boolean {
  const normalized = commitMessage.trim().toLowerCase();
  if (normalized === '') return false;
  return pullRequests.some((pullRequest) => {
    const title = pullRequest.title.trim().toLowerCase();
    return normalized === title || normalized === `#${pullRequest.number} ${title}`;
  });
}

function shouldShowFailureSection(item: DeploymentRecord): boolean {
  if (item.status === 'failure') return true;
  return item.status === 'cancelled' && (item.failureReason != null || item.failedJobName != null);
}

function failedPhaseLabel(item: DeploymentRecord): string {
  return item.phase ?? 'unknown';
}

export function DeploymentDrawer({
  item,
  onClose,
  canMutate = false,
  onRollback,
}: {
  item: DeploymentRecord;
  onClose: () => void;
  canMutate?: boolean;
  onRollback?: () => void;
}) {
  const duration = totalDurationSeconds(item);
  const showRollback = canMutate && onRollback != null && canRollbackDeployment(item);
  const showCommitMessage =
    item.commitMessage != null && !commitMessageMatchesPullRequest(item.commitMessage, item.pullRequests);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <>
      <div className='fixed inset-0 z-50 bg-black/40 sm:bg-black/30' onClick={onClose} />
      <aside
        aria-label={`${item.environment} ${item.mode} deployment details`}
        className='fixed inset-0 z-[60] flex w-full flex-col overflow-hidden overscroll-none bg-white sm:inset-y-0 sm:left-auto sm:right-0 sm:max-w-md sm:overscroll-auto sm:border-l sm:border-neutral-200 sm:shadow-2xl'
        role='dialog'
      >
        <div className='flex items-start justify-between gap-3 border-b border-neutral-100 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:px-5 sm:py-4'>
          <div className='min-w-0'>
            <p className='text-xs font-medium uppercase tracking-wide text-neutral-400'>
              {item.environment} · {item.mode}
            </p>
            <h2 className='mt-1 text-lg font-medium text-neutral-900'>Deployment timings</h2>
            <p className='mt-1 text-xs text-neutral-500'>
              {item.status.replace(/_/g, ' ')} · started {relativeTime(item.startedAt)}
            </p>
            <a
              href={item.workflowRunUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='mt-1 inline-block font-mono text-xs text-blue-700 hover:underline'
            >
              Workflow run {item.workflowRunId}
            </a>
          </div>
          <button
            aria-label='Close deployment details'
            className='inline-flex size-11 shrink-0 items-center justify-center rounded-md text-2xl leading-none text-neutral-400 hover:bg-neutral-50 hover:text-neutral-700 sm:size-8 sm:text-xl'
            onClick={onClose}
            type='button'
          >
            ×
          </button>
        </div>

        <div className='min-h-0 flex-1 space-y-6 overflow-y-auto overscroll-contain p-4 sm:p-5'>
          <DeploymentTimeline record={item} />

          {shouldShowFailureSection(item) && <FailureSection item={item} />}

          <section className='space-y-2'>
            <h3 className='text-xs font-medium uppercase tracking-wide text-neutral-500'>Steps</h3>
            <dl className='divide-y divide-neutral-100 overflow-hidden rounded-lg border border-neutral-200'>
              <TimingRow label='Started' value={formatClock(item.startedAt)} />
              <TimingRow label='Build' value={formatDuration(item.buildDurationSeconds)} />
              <TimingRow label='Deploy' value={formatDuration(item.deployDurationSeconds)} />
              <TimingRow label='Total' value={formatDuration(duration)} />
              <TimingRow
                label='Completed'
                value={item.completedAt != null ? formatClock(item.completedAt) : 'In progress'}
              />
            </dl>
          </section>

          <section className='space-y-3'>
            <h3 className='text-xs font-medium uppercase tracking-wide text-neutral-500'>Changes</h3>
            <div className='space-y-1'>
              <a
                href={`${GITHUB_COMMIT_URL}/${item.commitSha}`}
                target='_blank'
                rel='noopener noreferrer'
                className='block font-mono text-sm text-blue-700 hover:underline'
              >
                {item.commitShaShort}
              </a>
              {showCommitMessage && <p className='text-sm text-neutral-600'>{item.commitMessage}</p>}
            </div>
            {item.pullRequests.length > 0 && <DeploymentPullRequests pullRequests={item.pullRequests} />}
          </section>
        </div>

        <div className='flex flex-col gap-2 border-t border-neutral-100 bg-white px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:flex-row sm:flex-wrap sm:items-center sm:px-5'>
          <a
            href={item.workflowRunUrl}
            target='_blank'
            rel='noopener noreferrer'
            className={`${ACTION_BUTTON_CLASS} border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50`}
          >
            View workflow
          </a>
          {showRollback && (
            <button
              type='button'
              className={`${ACTION_BUTTON_CLASS} border border-red-200 bg-white text-red-700 hover:bg-red-50`}
              onClick={onRollback}
            >
              Rollback to here
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

function FailureSection({ item }: { item: DeploymentRecord }) {
  return (
    <section className='space-y-2 rounded-lg border border-red-200 bg-red-50 p-4' aria-label='Why it failed'>
      <h3 className='text-xs font-semibold uppercase tracking-wide text-red-800'>Why it failed</h3>
      <p className='text-sm font-medium text-red-900'>Failed during the {failedPhaseLabel(item)} step</p>
      {item.failedJobName != null && (
        <p className='text-sm text-red-900'>
          Job: <span className='font-medium'>{item.failedJobName}</span>
        </p>
      )}
      {item.failureReason != null ? (
        <p className='text-sm text-red-900'>{item.failureReason}</p>
      ) : (
        <p className='text-sm text-red-800'>
          No failure reason was recorded for this deploy.{' '}
          <a
            href={item.workflowRunUrl}
            target='_blank'
            rel='noopener noreferrer'
            className='font-medium text-red-900 underline hover:no-underline'
          >
            View workflow
          </a>{' '}
          for job logs.
        </p>
      )}
      {item.failureLogExcerpt != null && (
        <pre className='max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-red-200 bg-white p-2 font-mono text-xs text-red-950'>
          {item.failureLogExcerpt}
        </pre>
      )}
    </section>
  );
}

function TimingRow({ label, value }: { label: string; value: string }) {
  return (
    <div className='flex items-center justify-between gap-3 bg-white px-3 py-2.5'>
      <dt className='text-sm text-neutral-500'>{label}</dt>
      <dd className='text-sm font-medium tabular-nums text-neutral-900'>{value}</dd>
    </div>
  );
}
